import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { db, storage } from '../services/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
    collection, addDoc, serverTimestamp, query, orderBy, getDocs, onSnapshot,
    updateDoc, doc, where, limit, deleteDoc
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { submitIdentification } from '../services/identificationService';
import imageCompression from 'browser-image-compression';
import './RegressionToolPage.css';

const PROXY_BASE = 'https://proxyimage-hlhgvkwroq-ew.a.run.app';
const getProxyUrl = (url) => {
    if (!url) return '';
    if (url.includes('firebasestorage.googleapis.com') || url.startsWith('blob:')) return url;
    return `${PROXY_BASE}?url=${encodeURIComponent(url)}`;
};

function RegressionToolPage() {
    const { user, userProfile } = useAuth();

    // ── Core state
    const [activeSample, setActiveSample] = useState(null);
    const [pipelineResult, setPipelineResult] = useState(null);
    const [pipelineStatus, setPipelineStatus] = useState('idle'); // idle | running | done | error

    // ── Upload / drag-drop
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const dropZoneRef = useRef(null);

    // ── Labeling state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedManufacturer, setSelectedManufacturer] = useState(null);
    const [markGallery, setMarkGallery] = useState([]);
    const [galleryLoading, setGalleryLoading] = useState(false);
    const [selectedMarkId, setSelectedMarkId] = useState(null);
    const [notInDatabase, setNotInDatabase] = useState(false);
    const [savingLabel, setSavingLabel] = useState(false);
    const [labelSaved, setLabelSaved] = useState(false);

    // ── History
    const [historySamples, setHistorySamples] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');

    const identificationUnsubRef = useRef(null);

    // ════════════════════════════════════════════════════════════
    // ALL EFFECTS — must be before any conditional return
    // ════════════════════════════════════════════════════════════

    // History listener
    useEffect(() => {
        if (userProfile?.role !== 'admin') return;
        const q = query(collection(db, 'regression_samples'), orderBy('submittedAt', 'desc'));
        const unsub = onSnapshot(q, (snapshot) => {
            setHistorySamples(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setHistoryLoading(false);
        });
        return () => unsub();
    }, [userProfile]);

    // Cleanup identification listener on unmount
    useEffect(() => {
        return () => {
            if (identificationUnsubRef.current) {
                identificationUnsubRef.current();
                identificationUnsubRef.current = null;
            }
        };
    }, []);

    // Global paste handler (Ctrl+V)
    useEffect(() => {
        const handlePaste = (e) => {
            if (!user || userProfile?.role !== 'admin') return;
            const items = Array.from(e.clipboardData?.items || []);
            const imageItem = items.find(item => item.type.startsWith('image/'));
            if (imageItem) {
                const file = imageItem.getAsFile();
                if (file) handleFiles([file]);
            }
        };
        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, userProfile]);

    // Debounced manufacturer search
    useEffect(() => {
        if (!searchQuery.trim() || notInDatabase) {
            setSearchResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const searchLower = searchQuery.toLowerCase();
                const q = query(
                    collection(db, 'marks'),
                    where('manufacturer_lowercase', '>=', searchLower),
                    where('manufacturer_lowercase', '<=', searchLower + '\uf8ff'),
                    orderBy('manufacturer_lowercase'),
                    limit(50)
                );
                const snapshot = await getDocs(q);
                const uniqueMfrs = new Map();
                snapshot.docs.forEach(d => {
                    const data = d.data();
                    if (!uniqueMfrs.has(data.manufacturer_lowercase)) {
                        uniqueMfrs.set(data.manufacturer_lowercase, data.manufacturer);
                    }
                });
                setSearchResults(Array.from(uniqueMfrs.values()));
            } catch (err) {
                console.error('Search error:', err);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery, notInDatabase]);

    // ════════════════════════════════════════════════════════════
    // GUARD — after all hooks
    // ════════════════════════════════════════════════════════════
    if (userProfile?.role !== 'admin') {
        return <div style={{ padding: '20px', color: 'var(--color-text-primary)' }}>Access Denied</div>;
    }

    // ── Stats
    const totalSamples = historySamples.length;
    const labeledCount = historySamples.filter(s => s.status === 'labeled').length;
    const notInDbCount = historySamples.filter(s => s.label?.notInDatabase).length;

    // ════════════════════════════════════════════════════════════
    // HELPERS
    // ════════════════════════════════════════════════════════════

    const resetLabelingState = () => {
        setSearchQuery('');
        setSearchResults([]);
        setSelectedManufacturer(null);
        setMarkGallery([]);
        setSelectedMarkId(null);
        setNotInDatabase(false);
        setLabelSaved(false);
    };

    const handleFiles = async (files) => {
        const file = files[0];
        if (!file || !file.type.startsWith('image/')) return;

        setUploading(true);
        resetLabelingState();
        setPipelineStatus('idle');
        setPipelineResult(null);

        try {
            // Compress
            let compressed = file;
            try {
                compressed = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 2048, useWebWorker: true, fileType: 'image/jpeg', initialQuality: 0.85 });
            } catch { /* use original if compression fails */ }

            // Upload to regression_samples storage
            const timestamp = Date.now();
            const storagePath = `regression_samples/${user.uid}/${timestamp}-${file.name || 'paste.jpg'}`;
            const storageRef = ref(storage, storagePath);
            await uploadBytes(storageRef, compressed);
            const downloadUrl = await getDownloadURL(storageRef);

            // Create Firestore regression_samples doc
            const docRef = await addDoc(collection(db, 'regression_samples'), {
                uploadedImageUrl: downloadUrl,
                storagePath,
                submittedAt: serverTimestamp(),
                submittedBy: user.uid,
                status: 'pending_label',
                label: null,
                lastEvaluation: null
            });

            setActiveSample({ id: docRef.id, uploadedImageUrl: downloadUrl });
            setLabelSaved(false);

            // Auto-run pipeline in background
            runPipeline(downloadUrl);

        } catch (error) {
            console.error('Upload error:', error);
            alert('Upload failed: ' + error.message);
            setPipelineStatus('idle');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const runPipeline = async (imageUrl) => {
        // Cancel any previous listener
        if (identificationUnsubRef.current) {
            identificationUnsubRef.current();
            identificationUnsubRef.current = null;
        }

        setPipelineStatus('running');

        try {
            const sessionId = crypto.randomUUID();
            const identId = await submitIdentification({
                sessionId,
                category: 'porcelain',
                images: [{ url: imageUrl, name: 'regression_sample.jpg' }],
                mainImageUrl: imageUrl,
                status: 'pending',
                _isRegressionTest: true
            });

            // The cloud function updates status to 'completed'/'failed' and sets hallmarkMatches
            const unsub = onSnapshot(doc(db, 'identifications', identId), (snap) => {
                const data = snap.data();
                if (!data) return;

                const isDone = data.status === 'completed' || data.status === 'failed' ||
                               data.status === 'pending_approval' || data.status === 'approved';

                if (isDone) {
                    setPipelineStatus(data.status === 'failed' ? 'error' : 'done');
                    // The result stores matches as hallmarkMatches (see ResultsPage)
                    if (data.hallmarkMatches && data.hallmarkMatches.length > 0) {
                        setPipelineResult({ hallmarkMatches: data.hallmarkMatches });
                    } else {
                        setPipelineResult(null);
                    }
                    identificationUnsubRef.current?.();
                    identificationUnsubRef.current = null;
                }
            });

            identificationUnsubRef.current = unsub;

            // 3-minute timeout
            setTimeout(() => {
                if (identificationUnsubRef.current) {
                    identificationUnsubRef.current();
                    identificationUnsubRef.current = null;
                    setPipelineStatus(prev => prev === 'running' ? 'error' : prev);
                }
            }, 180000);

        } catch (err) {
            console.error('Pipeline run failed:', err);
            setPipelineStatus('error');
        }
    };

    // One-click confirm pipeline result
    const handleConfirmPipeline = async (match) => {
        if (!activeSample || !match) return;
        setSavingLabel(true);
        try {
            await updateDoc(doc(db, 'regression_samples', activeSample.id), {
                status: 'labeled',
                label: {
                    notInDatabase: false,
                    correctMarkId: match.id,
                    correctManufacturer: match.manufacturer,
                    correctPeriodStart: match.periodStart || null,
                    correctPeriodEnd: match.periodEnd || null,
                    confirmedFromPipeline: true,
                }
            });
            setLabelSaved(true);
            setTimeout(() => {
                setActiveSample(null);
                setPipelineResult(null);
                setPipelineStatus('idle');
                setLabelSaved(false);
            }, 900);
        } catch (error) {
            alert('Failed to save: ' + error.message);
        } finally {
            setSavingLabel(false);
        }
    };

    // Save manual label
    const handleSaveLabel = async () => {
        if (!activeSample) return;
        setSavingLabel(true);
        try {
            let labelData = { notInDatabase };
            if (!notInDatabase) {
                const selectedMark = markGallery.find(m => m.id === selectedMarkId);
                if (!selectedMark) throw new Error('No mark selected');
                labelData = {
                    ...labelData,
                    correctMarkId: selectedMark.id,
                    correctManufacturer: selectedMark.manufacturer,
                    correctPeriodStart: selectedMark.periodStart || null,
                    correctPeriodEnd: selectedMark.periodEnd || null,
                    confirmedFromPipeline: false,
                };
            }
            await updateDoc(doc(db, 'regression_samples', activeSample.id), {
                status: 'labeled',
                label: labelData
            });
            setLabelSaved(true);
            setTimeout(() => {
                setActiveSample(null);
                setPipelineResult(null);
                setPipelineStatus('idle');
                setLabelSaved(false);
            }, 900);
        } catch (error) {
            alert('Failed to save label: ' + error.message);
        } finally {
            setSavingLabel(false);
        }
    };

    // Drag-drop
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) handleFiles(files);
    };

    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = (e) => {
        if (!dropZoneRef.current?.contains(e.relatedTarget)) setIsDragging(false);
    };

    // Select manufacturer from search
    const handleSelectManufacturer = async (mfr) => {
        setSearchQuery(mfr);
        setSearchResults([]);
        setSelectedManufacturer(mfr);
        setSelectedMarkId(null);
        setGalleryLoading(true);
        try {
            const q = query(collection(db, 'marks'), where('manufacturer', '==', mfr), limit(50));
            const snap = await getDocs(q);
            setMarkGallery(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error(err);
        } finally {
            setGalleryLoading(false);
        }
    };

    // Edit existing sample
    const handleEditSample = (sample) => {
        resetLabelingState();
        setPipelineResult(null);
        setPipelineStatus('idle');
        setActiveSample({ id: sample.id, uploadedImageUrl: sample.uploadedImageUrl });

        if (sample.label?.notInDatabase) {
            setNotInDatabase(true);
        } else if (sample.label?.correctManufacturer) {
            setSearchQuery(sample.label.correctManufacturer);
            setSelectedManufacturer(sample.label.correctManufacturer);
            setSelectedMarkId(sample.label.correctMarkId || null);
            setGalleryLoading(true);
            getDocs(query(collection(db, 'marks'), where('manufacturer', '==', sample.label.correctManufacturer), limit(50)))
                .then(snap => {
                    setMarkGallery(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                    setGalleryLoading(false);
                }).catch(() => setGalleryLoading(false));
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteSample = async (sample) => {
        if (!window.confirm('Delete this sample?')) return;
        try {
            await deleteDoc(doc(db, 'regression_samples', sample.id));
            if (sample.storagePath) {
                try { await deleteObject(ref(storage, sample.storagePath)); } catch { /* ignore */ }
            }
            if (activeSample?.id === sample.id) {
                setActiveSample(null);
                setPipelineResult(null);
            }
        } catch (error) {
            alert('Delete failed: ' + error.message);
        }
    };

    const handleExportCSV = () => {
        const headers = ['id', 'uploadedImageUrl', 'correctManufacturer', 'correctMarkId', 'notInDb', 'status', 'submittedAt', 'brandRank', 'exactRank'];
        let csv = headers.join(',') + '\n';
        historySamples.forEach(row => {
            const dateStr = row.submittedAt ? new Date(row.submittedAt.toMillis()).toISOString() : '';
            const mfr = row.label?.notInDatabase ? 'NOT_IN_DB' : (row.label?.correctManufacturer || '');
            csv += [
                row.id,
                `"${row.uploadedImageUrl}"`,
                `"${mfr}"`,
                row.label?.correctMarkId || '',
                row.label?.notInDatabase ? 'true' : 'false',
                row.status,
                dateStr,
                row.lastEvaluation?.brandRank || '',
                row.lastEvaluation?.exactRank || '',
            ].join(',') + '\n';
        });
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'regression_samples.csv';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
    };

    const filteredHistory = historySamples.filter(s =>
        filterStatus === 'all' ? true : s.status === filterStatus
    );

    const canSaveManual = notInDatabase || (selectedManufacturer && selectedMarkId);

    // ════════════════════════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════════════════════════
    return (
        <div className="regression-tool-page">
            <div className="container">

                {/* Header */}
                <div className="page-header">
                    <div className="header-content">
                        <Link to="/admin" className="back-link">← Back to Dashboard</Link>
                        <h1>Regression Labeling Tool</h1>
                        <p>Build the ground-truth dataset — target: 100–200 labeled samples</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn btn-secondary btn-sm" onClick={handleExportCSV}>⬇ Export CSV</button>
                    </div>
                </div>

                {/* Progress Banner */}
                <div className="progress-banner">
                    <div className="progress-bar-track">
                        <div className="progress-bar-fill-main" style={{ width: `${Math.min((labeledCount / 100) * 100, 100)}%` }} />
                    </div>
                    <div className="progress-stats">
                        <span className="stat-pill labeled">{labeledCount} labeled</span>
                        <span className="stat-pill notindb">{notInDbCount} NOT_IN_DB</span>
                        <span className="stat-pill total">{totalSamples} total</span>
                        <span className="stat-pill target">target: 100</span>
                    </div>
                </div>

                <div className="regression-layout">

                    {/* ── LEFT PANEL ── */}
                    <div className="panel-left">

                        {/* Drop Zone */}
                        <div
                            ref={dropZoneRef}
                            className={`upload-drop-zone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onClick={() => !uploading && fileInputRef.current?.click()}
                        >
                            <input type="file" hidden ref={fileInputRef} accept="image/*"
                                onChange={e => { handleFiles(Array.from(e.target.files)); }} />
                            {uploading ? (
                                <><div className="spinner" /><p className="drop-title">Uploading…</p></>
                            ) : (
                                <>
                                    <span className="drop-icon">📷</span>
                                    <p className="drop-title">Drop, click, or paste (Ctrl+V)</p>
                                    <p className="drop-hint">Drag a hallmark photo here to start labeling</p>
                                </>
                            )}
                        </div>

                        {/* Active Sample Card */}
                        {activeSample && (
                            <div className={`active-sample-card ${labelSaved ? 'saved-flash' : ''}`}>

                                {/* Uploaded image */}
                                <div className="active-sample-image-wrap">
                                    <img src={activeSample.uploadedImageUrl} alt="Sample" />
                                </div>

                                {/* Pipeline Result */}
                                <div className="pipeline-preview">
                                    <div className="pipeline-header">
                                        <span>🤖 Pipeline result</span>
                                        <span className={`pipeline-badge ${pipelineStatus}`}>
                                            {pipelineStatus === 'idle' && '–'}
                                            {pipelineStatus === 'running' && '⏳ Running…'}
                                            {pipelineStatus === 'done' && '✓ Done'}
                                            {pipelineStatus === 'error' && '✗ Error / timeout'}
                                        </span>
                                    </div>

                                    {pipelineStatus === 'running' && (
                                        <div className="pipeline-running-bar">
                                            <div className="pipeline-running-fill" />
                                        </div>
                                    )}

                                    {pipelineStatus === 'done' && pipelineResult?.hallmarkMatches?.length > 0 && (
                                        <>
                                            <div className="pipeline-matches">
                                                {pipelineResult.hallmarkMatches.slice(0, 3).map((m, i) => (
                                                    <div key={m.id || i} className={`pipeline-match ${i === 0 ? 'top-match' : ''}`}>
                                                        <div className="match-left">
                                                            {(m.markImageUrl || m.imageUrl) && (
                                                                <img src={getProxyUrl(m.markImageUrl || m.imageUrl)} alt="" className="match-thumb" />
                                                            )}
                                                            <div className="match-info">
                                                                <span className="match-rank">#{i + 1}</span>
                                                                <span className="match-name">{m.manufacturer}</span>
                                                                <span className="match-score">{m.matchScore ? `${Math.round(m.matchScore)}%` : ''}</span>
                                                            </div>
                                                        </div>
                                                        <button
                                                            className={`btn-confirm ${i === 0 ? 'primary' : 'secondary'}`}
                                                            disabled={savingLabel}
                                                            onClick={() => handleConfirmPipeline(m)}
                                                        >
                                                            {i === 0 ? '✓ Correct' : 'Select'}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            <p className="pipeline-hint">
                                                If the pipeline got it right, click <strong>✓ Correct</strong>. Otherwise label manually below.
                                            </p>
                                        </>
                                    )}

                                    {pipelineStatus === 'done' && !pipelineResult && (
                                        <p className="pipeline-hint">Pipeline returned no matches. Please label manually.</p>
                                    )}
                                </div>

                                {/* Manual Label Section */}
                                <div className="manual-label-section">
                                    <div className="section-divider"><span>Manual Label</span></div>

                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={notInDatabase}
                                            onChange={e => {
                                                const checked = e.target.checked;
                                                setNotInDatabase(checked);
                                                if (checked) {
                                                    setSearchQuery('');
                                                    setSearchResults([]);
                                                    setSelectedManufacturer(null);
                                                    setMarkGallery([]);
                                                    setSelectedMarkId(null);
                                                }
                                            }}
                                        />
                                        <span>Mark NOT in database</span>
                                    </label>

                                    {!notInDatabase && (
                                        <div className="search-mfr-box">
                                            <input
                                                type="text"
                                                placeholder="Search manufacturer name…"
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                            />
                                            {searching && <span className="search-spinner">⏳</span>}
                                            {searchResults.length > 0 && (
                                                <ul className="search-dropdown">
                                                    {searchResults.map(mfr => (
                                                        <li key={mfr} onClick={() => handleSelectManufacturer(mfr)}>{mfr}</li>
                                                    ))}
                                                </ul>
                                            )}
                                            {galleryLoading && <div className="spinner small" style={{ margin: '8px auto' }} />}
                                            {selectedManufacturer && markGallery.length > 0 && (
                                                <>
                                                    <p className="gallery-label">Select the specific mark variant:</p>
                                                    <div className="mark-gallery">
                                                        {markGallery.map(m => (
                                                            <div
                                                                key={m.id}
                                                                className={`gallery-item ${selectedMarkId === m.id ? 'selected' : ''}`}
                                                                onClick={() => setSelectedMarkId(m.id)}
                                                                title={m.description || m.manufacturer}
                                                            >
                                                                <img src={getProxyUrl(m.markImageUrl || m.imageUrl)} alt={m.manufacturer} />
                                                                {selectedMarkId === m.id && <span className="gallery-check">✓</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                            {selectedManufacturer && markGallery.length === 0 && !galleryLoading && (
                                                <p className="pipeline-hint" style={{marginTop:'0.5rem'}}>No mark images found for this manufacturer.</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Save button — always visible when active sample exists */}
                                    <button
                                        className="btn btn-primary save-btn"
                                        disabled={savingLabel || !canSaveManual}
                                        onClick={handleSaveLabel}
                                        style={{ marginTop: '1rem' }}
                                    >
                                        {savingLabel ? 'Saving…' :
                                         notInDatabase ? '💾 Save as NOT_IN_DB' :
                                         selectedMarkId ? '💾 Save Label' :
                                         selectedManufacturer ? 'Select a mark variant above' :
                                         'Search manufacturer above'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── RIGHT PANEL ── */}
                    <div className="panel-right">
                        <div className="panel-header-row">
                            <div className="filter-tabs">
                                {['all', 'labeled', 'pending_label'].map(f => (
                                    <button
                                        key={f}
                                        className={`filter-tab ${filterStatus === f ? 'active' : ''}`}
                                        onClick={() => setFilterStatus(f)}
                                    >
                                        {f === 'all' ? `All (${totalSamples})` :
                                         f === 'labeled' ? `Labeled (${labeledCount})` :
                                         `Pending (${totalSamples - labeledCount})`}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {historyLoading ? (
                            <div className="spinner" style={{ margin: '2rem auto' }} />
                        ) : filteredHistory.length === 0 ? (
                            <div className="empty-state">
                                <span>📭</span>
                                <p>{filterStatus === 'all'
                                    ? 'No samples yet. Upload your first hallmark photo.'
                                    : 'No samples in this category.'}</p>
                            </div>
                        ) : (
                            <div className="sample-grid">
                                {filteredHistory.map(sample => {
                                    const brandRank = sample.lastEvaluation?.brandRank;
                                    const isHit = brandRank === 1;
                                    const isMiss = brandRank === -1;
                                    return (
                                        <div
                                            key={sample.id}
                                            className={`sample-card ${activeSample?.id === sample.id ? 'active' : ''}`}
                                        >
                                            <div className="sample-card-image" onClick={() => handleEditSample(sample)}>
                                                <img src={sample.uploadedImageUrl} alt="" />
                                                <span className={`status-dot ${sample.status}`} title={sample.status} />
                                            </div>
                                            <div className="sample-card-body">
                                                <p className="sample-mfr" title={sample.label?.correctManufacturer}>
                                                    {sample.label?.notInDatabase
                                                        ? <em className="not-in-db-tag">NOT_IN_DB</em>
                                                        : (sample.label?.correctManufacturer || <span className="unlabeled-tag">unlabeled</span>)
                                                    }
                                                </p>
                                                {brandRank !== undefined && brandRank !== null && (
                                                    <span className={`rank-pill ${isHit ? 'hit' : isMiss ? 'miss' : 'partial'}`}>
                                                        {sample.label?.notInDatabase
                                                            ? (isHit ? '✓ Rejected' : '✗ Not rejected')
                                                            : (isHit ? '✓ Rank 1' : brandRank > 0 ? `Rank ${brandRank}` : '✗ Miss')}
                                                    </span>
                                                )}
                                                <div className="sample-card-actions">
                                                    <button className="btn-icon-sm" title="Edit" onClick={() => handleEditSample(sample)}>✏️</button>
                                                    <button className="btn-icon-sm danger" title="Delete" onClick={() => handleDeleteSample(sample)}>🗑</button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
}

export default RegressionToolPage;
